/**
 * Public Lane Booking View (read-only)
 *
 * Anyone can see current lane availability. Clicking an empty slot prompts
 * the visitor to sign in / sign up before reserving. Names, coach names and
 * block reasons are already scrubbed server-side for non-admins.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Calendar, ChevronLeft, ChevronRight, ArrowLeft, Loader2, Lock } from 'lucide-react';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addDays = (dateStr, delta) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const formatDateHeading = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (t) => {
  if (!t || typeof t !== 'string' || !t.includes(':')) return '—';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

export default function PublicLaneBooking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [availability, setAvailability] = useState(null);
  const [businessLogo, setBusinessLogo] = useState(null);

  // `?embedded=1` hides the top header + footer so the grid fits cleanly
  // inside a third-party iframe (see /app/embed/).
  const isEmbedded = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('embedded') === '1';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/logo`);
        if (res.ok) {
          const data = await res.json();
          if (data.logo_url) setBusinessLogo(`${API_URL}${data.logo_url}`);
        }
      } catch {}
    })();
  }, []);

  // Guards against stale responses arriving out-of-order on rapid date clicks.
  const availabilityRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++availabilityRequestId.current;
    setLoading(true);
    fetch(`${API_URL}/api/lanes/availability?date=${selectedDate}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (requestId !== availabilityRequestId.current) return;
        setAvailability(data);
      })
      .finally(() => {
        if (requestId === availabilityRequestId.current) setLoading(false);
      });
  }, [selectedDate]);

  const handleBookClick = () => {
    if (user) {
      // Logged in → send them to their role's book page
      const target =
        user.role === 'customer'
          ? '/book'
          : user.role === 'parent'
            ? '/parent/book-lane'
            : user.role === 'player'
              ? '/player/book-lane'
              : '/';
      if (isEmbedded) {
        window.open(`${window.location.origin}${target}`, '_blank', 'noopener');
      } else {
        navigate(target);
      }
    } else {
      // Anonymous → go to sign up with a redirect back
      const authUrl = `/?tab=signup&redirect=${encodeURIComponent('/book')}`;
      if (isEmbedded) {
        window.open(`${window.location.origin}${authUrl}`, '_blank', 'noopener');
      } else {
        navigate(authUrl);
      }
    }
  };

  const openExternal = (path) => {
    if (isEmbedded) {
      window.open(`${window.location.origin}${path}`, '_blank', 'noopener');
    } else {
      navigate(path);
    }
  };

  // Group the lane's consecutive same-reason blocks/bookings so the grid
  // renders a single "Reserved"/"Booked" cell across rowspan slots.
  const spanInfoFor = (lane, idx) => {
    const slot = lane.slots[idx];
    if (!slot) return { skip: false, rowSpan: 1 };

    // Booked slots
    if (slot.booking_id) {
      const prev = lane.slots[idx - 1];
      if (prev && prev.booking_id === slot.booking_id) {
        return { skip: true, rowSpan: 0, type: 'booked' };
      }
      let span = 1;
      for (let i = idx + 1; i < lane.slots.length; i++) {
        if (lane.slots[i]?.booking_id === slot.booking_id) span++;
        else break;
      }
      return { skip: false, rowSpan: span, type: 'booked' };
    }
    // Blocked (coaching etc — reason redacted server-side for non-admins)
    if (!slot.is_available && slot.block_reason) {
      const prev = lane.slots[idx - 1];
      if (
        prev &&
        !prev.is_available &&
        !prev.booking_id &&
        prev.block_reason === slot.block_reason
      ) {
        return { skip: true, rowSpan: 0, type: 'block' };
      }
      let span = 1;
      for (let i = idx + 1; i < lane.slots.length; i++) {
        const n = lane.slots[i];
        if (n && !n.is_available && !n.booking_id && n.block_reason === slot.block_reason) span++;
        else break;
      }
      return { skip: false, rowSpan: span, type: 'block' };
    }
    return { skip: false, rowSpan: 1 };
  };

  const times = useMemo(
    () => availability?.lanes?.[0]?.slots?.map((s) => s?.start_time).filter(Boolean) || [],
    [availability],
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header — hidden when embedded as iframe on a third-party site */}
      {!isEmbedded && (
        <header className="bg-slate-900 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
            data-testid="back-home-btn"
          >
            {businessLogo ? (
              <img src={businessLogo} alt="Home" className="h-10 w-auto" />
            ) : (
              <>
                <ArrowLeft className="w-4 h-4" />
                <span>Back to home</span>
              </>
            )}
          </button>
          <div className="flex items-center gap-2">
            {!user && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-white/40 hover:bg-white hover:text-slate-900"
                  onClick={() => navigate('/?tab=login')}
                  data-testid="public-signin-btn"
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => navigate('/?tab=signup&redirect=/book')}
                  data-testid="public-signup-btn"
                >
                  Sign Up
                </Button>
              </>
            )}
            {user && (
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={handleBookClick}
                data-testid="go-to-booking-btn"
              >
                Go to My Bookings
              </Button>
            )}
          </div>
        </div>
      </header>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Lane Availability</h1>
          <p className="text-slate-600 mt-1">
            Browse open slots across our practice lanes.
            {!user && (
              <>
                {' '}
                <button
                  onClick={() => openExternal('/?tab=signup&redirect=/book')}
                  className="text-blue-600 hover:underline font-medium"
                  data-testid="inline-signup-link"
                >
                  Sign up
                </button>{' '}
                or{' '}
                <button
                  onClick={() => openExternal('/?tab=login')}
                  className="text-blue-600 hover:underline font-medium"
                  data-testid="inline-signin-link"
                >
                  sign in
                </button>{' '}
                to reserve a lane.
              </>
            )}
          </p>
        </div>

        {/* Pricing preview */}
        <Card className="mb-6">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-semibold text-slate-800">Lane 1 & 2</div>
              <div className="text-slate-600">30 min: $30 · 1 hr: $55 · 1.5 hr: $80 · 2 hr: $110</div>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Lane 3 & 4</div>
              <div className="text-slate-600">30 min: $25 · 1 hr: $50 · 1.5 hr: $75 · 2 hr: $100</div>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Cancellation</div>
              <div className="text-slate-600">Free 24+ hrs before · credit 6–24 hrs · no refund {'<'}6 hrs</div>
            </div>
          </CardContent>
        </Card>

        {/* Date navigation */}
        <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-lg shadow-sm">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedDate(addDays(selectedDate, -1))}
            disabled={selectedDate <= todayStr()}
            data-testid="prev-day-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <div>
              <div className="font-bold text-lg">{formatDateHeading(selectedDate)}</div>
              {availability?.business_hours && (
                <div className="text-xs text-slate-500">
                  Open {formatTime(availability.business_hours.open)} –{' '}
                  {formatTime(availability.business_hours.close)}
                </div>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedDate(addDays(selectedDate, 1))}
            data-testid="next-day-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Grid */}
        {loading && !availability ? (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600" />
            <p className="text-slate-500 mt-3">Loading availability…</p>
          </div>
        ) : !availability?.lanes?.length ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-slate-500">No lanes available for this date.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="relative">
            {/* Dimming overlay while refreshing to a new date — keeps the
                grid visible so users immediately know what's happening. */}
            {loading && (
              <div
                className="absolute inset-0 z-20 bg-white/60 backdrop-blur-sm flex items-center justify-center rounded-lg"
                data-testid="public-availability-refreshing-overlay"
              >
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-slate-200">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-slate-700">Loading {formatDateHeading(selectedDate)}…</span>
                </div>
              </div>
            )}
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full" data-testid="public-lane-grid">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Time</th>
                    {availability.lanes.map((lane, i) => (
                      <th
                        key={lane.lane_id}
                        className={`text-center px-4 py-3 text-sm font-semibold ${
                          i === 0
                            ? 'bg-blue-50 text-blue-800'
                            : i === 1
                              ? 'bg-emerald-50 text-emerald-800'
                              : i === 2
                                ? 'bg-purple-50 text-purple-800'
                                : 'bg-amber-50 text-amber-800'
                        }`}
                      >
                        {lane.lane_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {times.map((time, rowIdx) => (
                    <tr key={time} className="border-t border-slate-100 h-12 relative">
                      <td className="px-4 py-2 text-sm text-slate-700 font-medium whitespace-nowrap">
                        {formatTime(time)}
                      </td>
                      {availability.lanes.map((lane) => {
                        const slot = lane.slots[rowIdx];
                        const info = spanInfoFor(lane, rowIdx);
                        if (info.skip) return <td key={lane.lane_id} className="p-0" />;

                        const spanH =
                          info.rowSpan > 1
                            ? { height: `calc(${info.rowSpan * 100}% + ${info.rowSpan - 1}px)` }
                            : undefined;

                        return (
                          <td key={lane.lane_id} className="p-1 relative" style={{ minWidth: 140 }}>
                            {info.type === 'booked' ? (
                              <div
                                className={`w-full bg-slate-200 border-2 border-slate-300 rounded text-xs text-slate-600 flex items-center justify-center py-2 ${info.rowSpan > 1 ? 'absolute left-1 right-1 top-0 z-10' : ''}`}
                                style={spanH}
                              >
                                <span className="font-medium">Booked</span>
                              </div>
                            ) : info.type === 'block' ? (
                              <div
                                className={`w-full bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-center justify-center py-2 ${info.rowSpan > 1 ? 'absolute left-1 right-1 top-0 z-10' : ''}`}
                                style={spanH}
                              >
                                <span className="font-medium">{slot.block_reason || 'Reserved'}</span>
                              </div>
                            ) : slot?.is_available ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={handleBookClick}
                                data-testid={`public-book-${lane.lane_id}-${time}`}
                              >
                                {user ? (
                                  'Book'
                                ) : (
                                  <>
                                    <Lock className="w-3 h-3 mr-1.5" /> Book
                                  </>
                                )}
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {!user && (
          <Card className="mt-6 bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-lg font-semibold text-slate-900">Ready to reserve a lane?</div>
                <div className="text-sm text-slate-600">
                  Create a free account — takes less than a minute.
                </div>
              </div>
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => openExternal('/?tab=signup&redirect=/book')}
                data-testid="cta-signup-btn"
              >
                Sign Up to Book
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {!isEmbedded && <Footer />}
    </div>
  );
}
